import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import './Blog.css';

// Données mockées des articles de blog
const mockArticles = [
  {
    id: 1,
    title: "Les tendances du sport en 2024 : vers une pratique plus inclusive",
    excerpt: "Découvrez comment le monde du sport évolue pour devenir plus accessible et inclusif pour tous les pratiquants.",
    content: "Le sport connaît une révolution silencieuse mais profonde...",
    author: "Marie Dubois",
    publishDate: "2024-01-15",
    category: "Tendances",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600",
    tags: ["sport", "inclusion", "tendances", "2024"],
    readTime: "5 min"
  },
  {
    id: 2,
    title: "Comment choisir le bon coach sportif pour vos objectifs",
    excerpt: "Guide complet pour sélectionner le professionnel qui vous accompagnera vers la réussite de vos projets sportifs.",
    content: "Choisir un coach sportif est une décision importante...",
    author: "Thomas Martin",
    publishDate: "2024-01-10",
    category: "Conseils",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600",
    tags: ["coaching", "conseils", "objectifs"],
    readTime: "7 min"
  },
  {
    id: 3,
    title: "L'importance de la préparation mentale dans le sport",
    excerpt: "La performance sportive ne dépend pas uniquement du physique. Explorez l'impact crucial de la préparation mentale.",
    content: "La préparation mentale est souvent négligée...",
    author: "Sophie Laurent",
    publishDate: "2024-01-05",
    category: "Performance",
    image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600",
    tags: ["mental", "performance", "psychologie"],
    readTime: "6 min"
  },
  {
    id: 4,
    title: "Les bienfaits du sport en entreprise",
    excerpt: "Comment intégrer le sport dans votre environnement professionnel pour améliorer bien-être et productivité.",
    content: "Le sport en entreprise gagne en popularité...",
    author: "Pierre Moreau",
    publishDate: "2024-01-01",
    category: "Entreprise",
    image: "https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=600",
    tags: ["entreprise", "bien-être", "productivité"],
    readTime: "4 min"
  },
  {
    id: 5,
    title: "Nutrition sportive : les bases pour optimiser vos performances",
    excerpt: "Découvrez les principes fondamentaux d'une alimentation adaptée à votre pratique sportive.",
    content: "Une bonne nutrition est la clé de la performance...",
    author: "Julie Simon",
    publishDate: "2023-12-28",
    category: "Nutrition",
    image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600",
    tags: ["nutrition", "performance", "alimentation"],
    readTime: "8 min"
  },
  {
    id: 6,
    title: "Les nouvelles technologies au service du sport",
    excerpt: "Comment l'innovation technologique révolutionne l'entraînement et le suivi des performances sportives.",
    content: "La technologie transforme le monde du sport...",
    author: "Alexandre Roux",
    publishDate: "2023-12-25",
    category: "Technologie",
    image: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=600",
    tags: ["technologie", "innovation", "performance"],
    readTime: "6 min"
  }
];

const categories = ['Tous', 'Tendances', 'Conseils', 'Performance', 'Entreprise', 'Nutrition', 'Technologie'];

const Blog = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState(mockArticles);
  const [filteredArticles, setFilteredArticles] = useState(mockArticles);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 6;

  useEffect(() => {
    let filtered = articles;

    // Filtrer par catégorie
    if (selectedCategory !== 'Tous') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredArticles(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, articles]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleArticleClick = (articleId) => {
    navigate(`/blog/${articleId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Tendances': 'primary',
      'Conseils': 'success',
      'Performance': 'warning',
      'Entreprise': 'info',
      'Nutrition': 'secondary',
      'Technologie': 'error'
    };
    return colors[category] || 'default';
  };

  // Pagination
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);

  const breadcrumbItems = [
    { label: 'Accueil', path: '/' },
    { label: 'Actualités', path: '/blog' }
  ];

  return (
    <>
      <Helmet>
        <title>Actualités - Conseils Sport - Mission Sport</title>
        <meta name="description" content="Découvrez nos articles sur le sport, la nutrition, l'entraînement et les dernières tendances. Conseils d'experts pour optimiser vos performances." />
        <meta name="keywords" content="actualités sport, conseils entraînement, nutrition sportive, performance, coaching" />
        <meta property="og:title" content="Actualités Mission Sport - Conseils" />
        <meta property="og:description" content="Articles et conseils d'experts pour améliorer vos performances sportives" />
        <meta property="og:type" content="website" />
      </Helmet>

      <main className="blog-page">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Breadcrumbs */}
          <Box mb={3}>
            <Breadcrumbs items={breadcrumbItems} />
          </Box>

          {/* En-tête */}
          <Box mb={4} textAlign="center">
            <Typography variant="h2" component="h1" gutterBottom className="page-title">
              Actualités Mission Sport
            </Typography>
            <Typography variant="h5" color="text.secondary" className="page-subtitle">
              Actualités, conseils et tendances du monde du sport
            </Typography>
          </Box>

          {/* Barre de recherche et filtres */}
          <Box mb={4}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Rechercher un article..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '25px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={1} flexWrap="wrap" justifyContent={{ xs: 'center', md: 'flex-end' }}>
                  {categories.map((category) => (
                    <Chip
                      key={category}
                      label={category}
                      onClick={() => handleCategoryChange(category)}
                      color={selectedCategory === category ? 'primary' : 'default'}
                      variant={selectedCategory === category ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Articles */}
          {currentArticles.length > 0 ? (
            <>
              <Grid container spacing={4}>
                {currentArticles.map((article) => (
                  <Grid item xs={12} md={6} lg={4} key={article.id}>
                    <Card
                      className="article-card"
                      onClick={() => handleArticleClick(article.id)}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                        },
                        borderRadius: '16px',
                        overflow: 'hidden',
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={article.image}
                        alt={article.title}
                        sx={{
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          },
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box mb={2}>
                          <Chip
                            label={article.category}
                            color={getCategoryColor(article.category)}
                            size="small"
                            sx={{ mb: 1 }}
                          />
                        </Box>
                        
                        <Typography
                          variant="h6"
                          component="h3"
                          gutterBottom
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {article.title}
                        </Typography>
                        
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {article.excerpt}
                        </Typography>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {article.author}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {article.readTime}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(article.publishDate)}
                          </Typography>
                        </Box>
                        
                        <Button
                          variant="text"
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            mt: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            p: 0,
                            justifyContent: 'flex-start',
                          }}
                        >
                          Lire l'article
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={6}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: '12px',
                      },
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aucun article trouvé
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Essayez de modifier vos critères de recherche ou de sélectionner une autre catégorie.
              </Typography>
            </Box>
          )}
        </Container>
      </main>
    </>
  );
};

export default Blog;